import apiClient from "@/api/apiClient";
import backendClient from "@/api/backendClient";
import { notify } from "@/hooks/useNotifications";
import i18n from "@/providers/i18n";
import { GlobalRole, type UpdateUserProfile } from "@/supabase.types";

export const getAuthSession = async () => {
  const { data, error } = await apiClient.auth.getSession();
  if (error) throw error;
  return data.session;
};

export const getAuthClaims = async () => {
  const { data, error } = await apiClient.auth.getClaims();
  if (error) throw error;
  return data?.claims ?? null;
};

export const getAuthUser = async () => {
  const { data, error } = await apiClient.auth.getUser();
  if (error) throw error;
  return data.user;
};

export const getAuthUserProfile = async (id?: string) => {
  if (!id) return null;
  const { data, error } = await apiClient.from("user_profiles").select("*").eq("id", id).maybeSingle();
  if (error) throw error;
  return data;
};

export const getAuthUserPermissions = async (id?: string) => {
  if (!id) return null;

  const permissionResults = await Promise.all([
    apiClient.from("users_to_roles").select("*").eq("user_id", id).eq("role", GlobalRole.admin).maybeSingle(),
    apiClient.from("users_to_workspaces").select("*").eq("user_id", id),
    apiClient.from("users_to_deployments").select("*").eq("user_id", id),
  ]);
  if (permissionResults.some((res) => res.error)) throw new Error("User Permissions couldn't be fetched.");

  return {
    globalAdmin: permissionResults[0].data,
    workspace: permissionResults[1].data ?? [],
    deployment: permissionResults[2].data ?? [],
  };
};

export const getAuthUserAndProfileAndPermissions = async () => {
  try {
    const user = await getAuthUser();
    const profile = await getAuthUserProfile(user.id);
    const permissions = await getAuthUserPermissions(user.id);

    return { user, profile, permissions };
  } catch (error) {
    console.error(`Failed to fetch user: `, error);
    notify.error(i18n.t("notify.get.error", { e: i18n.t("user") }));
  }
};

export interface SignInProps {
  email: string;
  password: string;
}
export const signIn = async ({ email, password }: SignInProps) => {
  const _email = email.trim().toLowerCase();
  const { data, error } = await apiClient.auth.signInWithPassword({ email: _email, password });
  if (error) throw error;
  return data.user;
};

export const getDomainByEmail = (email?: string | null) => {
  if (!email || email === "") return null;
  return email.split("@")[1];
};
export interface SignInSSOProps {
  email: string;
}
export const signInSSO = async ({ email }: SignInSSOProps) => {
  const _email = email.trim().toLowerCase();
  const domain = getDomainByEmail(_email);
  if (!domain) throw new Error("SSO domain not found");

  const { data, error } = await apiClient.auth.signInWithSSO({
    domain: domain,
  });
  if (error) throw error;

  // redirect to SSO login page
  if (data.url) {
    location.href = data.url;
  }
};

export interface SignUpProps {
  email: string;
  password: string;
  whitelabelBrandKey?: string;
  appBaseUrl?: string;
  language?: string;
}
export const signUp = async ({ email, password, language, appBaseUrl, whitelabelBrandKey }: SignUpProps) => {
  const _email = email.trim().toLowerCase();
  const { error } = await backendClient.post<null>("/auth/register", {
    authenticated: false,
    body: {
      email: _email,
      password,
      language,
      whitelabelBrandKey,
      appBaseUrl,
    },
  });
  if (error) throw error;
  return true;
};

export const signOut = async () => {
  const { error } = await apiClient.auth.signOut();
  if (error) throw error;
  return true;
};

export interface ResetPasswordProps {
  email: string;
  whitelabelBrandKey?: string;
  appBaseUrl?: string;
  language?: string;
}
export const resetAuthPassword = async ({ email, language, appBaseUrl, whitelabelBrandKey }: ResetPasswordProps) => {
  const _email = email.trim().toLowerCase();
  const { error } = await backendClient.post<null>("/auth/reset-password", {
    authenticated: false,
    body: {
      email: _email,
      language,
      whitelabelBrandKey,
      appBaseUrl,
    },
  });
  if (error) throw error;
  return true;
};

export interface UpdateUserProps {
  email?: string;
  password?: string;
}
export const updateAuthUser = async ({ email, password }: UpdateUserProps) => {
  const _email = email ? email.trim().toLowerCase() : undefined;
  const { data, error } = await apiClient.auth.updateUser({
    email: _email,
    password,
  });
  if (error) throw error;
  return !!data;
};

export interface UpdateUserProfileProps extends Pick<
  UpdateUserProfile,
  "id" | "first_name" | "last_name" | "phone" | "locale" | "timezone"
> {
  id: string; // id is required
}
export const updateAuthUserProfile = async (values: UpdateUserProfileProps) => {
  const { data, error } = await apiClient
    .from("user_profiles")
    .update({
      first_name: values.first_name?.trim() ?? undefined,
      last_name: values.last_name?.trim() ?? undefined,
      phone: values.phone ?? undefined,
      locale: values.locale ?? undefined,
      timezone: values.timezone ?? undefined,
    })
    .match({ id: values.id })
    .select("*")
    .maybeSingle();
  if (error) throw error;
  return data;
};
